import { ArrowRight, LogOut } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";

import {
  BookingStage,
  BookingType,
  VenueConfig
} from "../../../../../../types/venues";
import { getDevice } from "../../../../../../utils/device";

import {
  formatTimeForLocale,
  getBookingStageFromKey,
  getNextBookingStageFromKey,
  getBookingStages as getVenueBookingStages,
  isSameCollinsDay,
  walkingBookings
} from "../../../../../../utils/booking";

import { ViewBooking } from "collins-js-sdk";
import {
  addOrUpdateBooking,
  editBooking
} from "../../../../../../APIs/Booking/UpdateBooking";
import {
  EDSSwipeout,
  EDSSwipeoutOption
} from "../../../../../../components/eds/EDSSwipeout";
import { useDiaryContext } from "../../../../../../hooks/useDiaryContext";
import { useUserContext } from "../../../../../../hooks/useUserContext";
import { BookingLabelsEnum } from "../../../../../../types/bookingEnums";
import { VenueGroupConfig } from "../../../../../../types/VenueGroup";
import { getBookingTimeAsString } from "../../../../../../utils/singleBooking";
import { PreviewBookingModalBeta } from "../../../Runsheet/PreviewBookingModalBeta/PreviewBookingModalBeta";
import EditBooking from "../../../SelectedBooking/EditBooking/EditBooking";
import {
  getCheckInWaitlistProps,
  updateBooking
} from "../../../SelectedBooking/Manage/SelectedBookingManageController";
import SendSms from "../../../SelectedBooking/Messages/SMS/SendSms";
import {
  BookingCardIcons,
  DiaryBookingCardActionButtons,
  DiaryBookingCardBookingTypeBadge,
  DiaryBookingCardBookingTypeInTimeSection,
  DiaryBookingCardCustomerComments,
  DiaryBookingCardCustomerNameSection,
  DiaryBookingCardCustomerNotes,
  DiaryBookingCardDetailedLabels,
  DiaryBookingCardDragButton,
  DiaryBookingCardOfflineIndicator,
  DiaryBookingCardPreorderBanner,
  DiaryBookingCardRunsheetDetailsRow,
  DiaryBookingCardStatusIndicator,
  DiaryBookingCardTableAndZoneInfo
} from "./components";
import { useDiaryBookingCardHandlers } from "./hooks/useDiaryBookingCardHandlers";

type bookingItem = ViewBooking &
  editBooking & {
    isDivider?: boolean;
    time?: string | null;
  };
type DiaryBookingCardProps = {
  index: number;
  style: React.CSSProperties;
  parent: {
    props: {
      bookings: bookingItem[];
      venueConfig: any;
      venueGroupConfig: any;
      onBookingChanged: (booking: any) => void;
      selectedView: string;
      selectedBooking: any;
      setSelectedBooking: (booking: any) => void;
      refreshBookings: () => void;
      isOnRunSheet: boolean;
      disableSwipe: boolean;
      setDisableSwipe: (disable: boolean) => void;
      showDetailedLabels?: boolean;
      activePopoverBookingId?: string | null;
      setActivePopoverBookingId?: (id: string | null) => void;
    };
  };
};

export const DiaryBookingCard = ({
  index,
  style,
  parent
}: DiaryBookingCardProps) => {
  const { user } = useUserContext();
  const diaryContext = useDiaryContext();
  const isDesktop = getDevice() === "desktop";
  const item: bookingItem = parent.props.bookings[index];
  const booking: bookingItem = item;

  // A booking is "actively waitlisted" only while it's still on the waitlist
  // (status in_progress/new). Once accepted, status changes to "complete" but
  // waitlisted flag remains true — we must not show waitlist UI in that case.
  const isActivelyWaitlisted =
    Boolean(booking.waitlisted) &&
    (booking.status === "in_progress" || booking.status === "new");

  // Derive popover state from shared activePopoverBookingId to ensure
  // only one popover is open across all cards in the list at a time
  const isPopoverOpen = parent.props.activePopoverBookingId === booking.id;
  const setIsPopoverOpen = useCallback(
    (open: boolean) => {
      parent.props.setActivePopoverBookingId?.(
        open ? (booking.id ?? null) : null
      );
    },
    [parent.props.setActivePopoverBookingId, booking.id]
  );

  // State for modals
  const [showSendSms, setShowSendSms] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditBooking, setShowEditBooking] = useState(false);
  const touchDragCloneRef = useRef<HTMLElement | null>(null);
  const isTouchDraggingRef = useRef(false);

  // Track optimistic stage update to prevent visual glitching
  const [optimisticStage, setOptimisticStage] = useState<
    string | null | undefined
  >(null);

  // Use custom hook for all drag button handlers
  const {
    handleDragButtonPointerDown,
    handleDragButtonPointerMove,
    handleDragButtonPointerUp,
    handleDragButtonPointerCancel,
    handleDragButtonLostPointerCapture,
    handleDragButtonDragStart,
    handleDragButtonDragEnd,
    handleDragButtonClick
  } = useDiaryBookingCardHandlers({
    booking,
    touchDragCloneRef,
    isTouchDraggingRef,
    onDragStateChange: parent.props.setDisableSwipe,
    onBookingChanged: parent.props.onBookingChanged
  });

  // Track if we're currently updating to prevent intermediate renders
  const [isUpdating, setIsUpdating] = useState(false);

  // Extracted function to reduce cognitive complexity
  const updateBookingStage = useCallback(
    (
      name: string,
      booking: ViewBooking,
      nextStage: BookingStage | null,
      nextStageWalkIn: BookingStage | null
    ) => {
      const { bookings, setDiaryContext } = diaryContext;

      if (!bookings) {
        return;
      }

      // Determine the target stage based on the action
      let targetStage: string | null;

      if (isDirectStageAction(name)) {
        // Direct stage updates (e.g., from dropdown or check-in)
        targetStage = name;
      } else if (name === "uncheck" || name === "awaiting") {
        // Uncheck booking - always clear stage
        targetStage = "";
      } else {
        // Original logic for swipeout actions (cycling through stages)
        targetStage = booking.walkIn
          ? (nextStageWalkIn?.id ?? null)
          : (nextStage?.id ?? null);
      }

      // Prevent duplicate requests - only block if updating to the same target
      if (isUpdating && optimisticStage === targetStage) {
        return;
      }

      // Set loading state immediately to prevent all interactions
      setIsUpdating(true);
      setOptimisticStage(targetStage);

      // When a waitlisted booking gets a stage, also set waitlist check-in props
      // to move it off the waiting list (status: Complete, time, area)
      const waitlistCheckInProps =
        booking.waitlisted && targetStage && targetStage !== ""
          ? getCheckInWaitlistProps(booking)
          : {};

      // Use the proper updateBooking function to get updated booking and bookings array
      const { updatedBookings, updatedBooking } = updateBooking({
        selectedBooking: booking,
        bookings,
        bookingPropsToSave: {
          currentStage: targetStage,
          ...waitlistCheckInProps
        }
      });

      // Optimistic update - update UI immediately for instant feedback
      setDiaryContext({ bookings: updatedBookings });

      // Persist to backend - this will reconcile with optimistic update
      addOrUpdateBooking({
        booking: updatedBooking,
        diaryContext
      })
        .then(() => {
          // Clear loading state after backend confirms
          setIsUpdating(false);
          setOptimisticStage(null);
        })
        .catch(() => {
          // Clear loading state even on error
          setIsUpdating(false);
          setOptimisticStage(null);
        });
    },
    [
      diaryContext,
      isUpdating,
      optimisticStage,
      setIsUpdating,
      setOptimisticStage
    ]
  );

  const selectedView = parent.props.selectedView;
  const venueConfig: VenueConfig = parent.props.venueConfig;
  const venueGroupConfig: VenueGroupConfig = parent.props.venueGroupConfig;
  const onBookingChanged: any = parent.props.onBookingChanged;
  const selectedBooking: ViewBooking | undefined = parent.props.selectedBooking;
  const setSelectedBooking: React.Dispatch<
    React.SetStateAction<ViewBooking | undefined>
  > = parent.props.setSelectedBooking;

  const selected: boolean = booking?.id === selectedBooking?.id;

  // Memoized click handlers for main booking card div
  const handleBookingCardClick = useCallback(() => {
    if ((parent.props as any).onBookingClick) {
      (parent.props as any).onBookingClick();
    } else {
      setSelectedBooking(selected ? undefined : booking);
    }
  }, [parent.props, setSelectedBooking, selected, booking]);

  const handleBookingCardKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        if ((parent.props as any).onBookingClick) {
          (parent.props as any).onBookingClick();
        } else {
          setSelectedBooking(selected ? undefined : booking);
        }
      }
    },
    [parent.props, setSelectedBooking, selected, booking]
  );

  // Modal close handlers - memoized to prevent re-renders
  const handleCloseSendSms = useCallback(() => {
    setShowSendSms(false);
  }, []);

  const handleSentSms = useCallback(() => {
    setShowSendSms(false);
  }, []);

  const handleClosePreviewModal = useCallback(() => {
    setShowPreviewModal(false);
  }, []);

  const handleCloseEditBooking = useCallback(() => {
    setShowEditBooking(false);
  }, []);

  if (item.isDivider) {
    return (
      <div
        className="DiaryBookingCard__divider"
        key={`divider-${item.time}-${index}`}
        style={{
          ...style, // must include height and width from react-virtualized
          display: "flex",
          alignItems: "center",
          height: 36,
          padding: 10,
          fontWeight: "bold",
          fontSize: 16,
          zIndex: 2
        }}
      >
        {item.time}
      </div>
    );
  }

  // Helper function to get current booking stages (lightweight version for swipeout logic)
  // The full stage calculation with optimistic updates is handled in DiaryBookingCardStatusIndicator
  const getCurrentStages = () => {
    const effectiveStage = booking.currentStage;

    let currentStage: BookingStage | null = getBookingStageFromKey(
      effectiveStage ?? "",
      venueConfig,
      venueGroupConfig
    );

    // Ensure we always have a currentStage
    currentStage ??= {
      id: effectiveStage ?? "",
      label: effectiveStage ? "Unknown Stage" : "Unchecked",
      colour: "6b7280",
      walk_in_default: false
    };

    const currentStageWalkIn: BookingStage | null = isSameCollinsDay(
      new Date(),
      new Date(booking.date ?? "")
    )
      ? walkingBookings(effectiveStage ?? "", venueConfig, venueGroupConfig)
      : currentStage;

    const nextStage: BookingStage | null = getNextBookingStageFromKey(
      currentStage ? currentStage.id : "",
      venueConfig,
      venueGroupConfig
    );

    const nextStageWalkIn: BookingStage | null = getNextBookingStageFromKey(
      currentStageWalkIn ? currentStageWalkIn.id : "",
      venueConfig,
      venueGroupConfig
    );

    return { currentStage, currentStageWalkIn, nextStage, nextStageWalkIn };
  };

  const { currentStage, currentStageWalkIn, nextStage, nextStageWalkIn } =
    getCurrentStages();

  // Helper function to create stage progression option
  const createStageOption = (
    stage: BookingStage | null
  ): EDSSwipeoutOption | null => {
    if (!stage) return null;

    const customColor = stage.colour
      ? `#${stage.colour.replace(/^#/, "")}`
      : undefined;

    return {
      name: stage.id,
      icon: <ArrowRight size={16} />,
      label: stage.label,
      accent: "teal",
      customColor
    };
  };

  // Helper function to get swipeout options
  const getSwipeoutOptions = () => {
    // On desktop, actively waitlisted bookings use inline action buttons instead of swipe
    // On mobile, keep swipe behavior for all bookings
    if (isDesktop && isActivelyWaitlisted) {
      return { leftOptions: [], rightOptions: [] };
    }

    const isToday = isSameCollinsDay(new Date(), new Date(booking.date ?? ""));
    const canShowOptions =
      currentStage?.id !== "left" &&
      currentStageWalkIn?.id !== "left" &&
      booking.id &&
      isToday;

    // Get the first available stage for unchecked bookings
    const stages =
      venueConfig && venueGroupConfig
        ? getVenueBookingStages({ venueConfig, venueGroupConfig })
        : [];

    const firstStage = stages.length > 0 ? stages[0] : null;

    // LEFT side of card (revealed when swiping right-to-left) shows stage progression
    const leftOptions: EDSSwipeoutOption[] = canShowOptions
      ? ([
          createStageOption(
            booking.walkIn
              ? (nextStageWalkIn ?? firstStage)
              : (nextStage ?? firstStage)
          )
        ].filter(Boolean) as EDSSwipeoutOption[])
      : [];

    // RIGHT side of card (revealed when swiping left-to-right) shows "Left" action
    const rightOptions: EDSSwipeoutOption[] = canShowOptions
      ? [
          {
            icon: <LogOut size={16} />,
            name: "left",
            label: "Left",
            accent: "blue"
          }
        ]
      : [];

    return { leftOptions, rightOptions };
  };

  const { leftOptions, rightOptions } = getSwipeoutOptions();

  // Helper function to get the first checked-in stage
  const getFirstCheckedInStage = () => {
    const stages =
      venueConfig && venueGroupConfig
        ? getVenueBookingStages({ venueConfig, venueGroupConfig })
        : [];

    // Return the first configured stage (no hardcoded assumptions)
    return stages.length > 0 ? stages[0].id : "";
  };

  // Helper function to get booking data
  const getBookingData = () => {
    const bookingType =
      venueConfig.booking_types && booking.type
        ? venueConfig.booking_types.find(
            (bookingType: BookingType) => bookingType.id === booking.type?.id
          )
        : null;

    return { bookingType };
  };

  const { bookingType } = getBookingData();

  // Convert EDSSwipeoutOption to EDSSwipeoutOption (preserve customColor)
  const convertToEDSOptions = (
    options: EDSSwipeoutOption[]
  ): EDSSwipeoutOption[] => {
    return options.map((option) => ({
      name: option.name,
      icon: option.icon,
      label: option.label,
      accent: option.accent,
      customColor: option.customColor
    }));
  };

  // Helper functions to reduce cognitive complexity
  const hasLinkedBookings = (): boolean => {
    return (
      (booking.linkedBookings?.length ?? 0) !== 0 ||
      booking.labels?.find(
        (LinkedBooking: string) =>
          LinkedBooking === BookingLabelsEnum.LinkedBooking
      ) === BookingLabelsEnum.LinkedBooking
    );
  };

  const hasIconContent = (): boolean => {
    if (booking.actualGuests) return true;
    if ((booking.labels?.length ?? 0) !== 0) return true;
    if (hasLinkedBookings()) return true;
    if (booking.company?.[0]) return true;
    if (booking.doubleConfirmed) return true;
    if ((booking.comments?.length ?? 0) !== 0) return true;
    if (booking.hasPreorders) return true;
    if ((booking.deposits?.length ?? 0) !== 0) return true;
    if (booking.notes || booking.offer?.id) return true;
    return false;
  };

  const hasBookingTypeBadge = (): boolean => {
    return Boolean(bookingType && !(parent.props as any).isOnRunSheet);
  };

  // Helper function to check if Row3 has any content
  const hasRow3Content = (): boolean => {
    // Check if booking is unsaved (shows offline indicator)
    if (booking.unsaved) {
      return true;
    }

    // Check if any icons should be displayed (only for main diary, not runsheet)
    if (!(parent.props as any).isOnRunSheet && hasIconContent()) {
      return true;
    }

    // Check if booking type badge should be shown (only for main diary, not runsheet)
    if (hasBookingTypeBadge()) {
      return true;
    }

    return false;
  };

  // Helper functions to reduce cognitive complexity
  const getCheckInWaitlistPropsIfNeeded = (booking: ViewBooking) => {
    return !booking?.currentStage ? getCheckInWaitlistProps(booking) : {};
  };

  const hasPreorderBanner = () => {
    return (parent.props as any).showDetailedLabels && booking.hasPreorders;
  };

  const isDirectStageAction = (name: string): boolean => {
    // Check venue-specific stages only (no hardcoded stages)
    const stages =
      venueConfig && venueGroupConfig
        ? getVenueBookingStages({ venueConfig, venueGroupConfig })
        : [];

    return stages.some((stage) => stage.id === name);
  };

  // Helper function to reduce cognitive complexity
  const getBookingCardClassName = () => {
    const baseClass = selected
      ? "DiarySidebarListItem_SelectedItem"
      : "DiarySidebarListItem";

    const viewClass =
      selectedView === "FloorplanTab"
        ? "DiarySidebarListItem--floorplan"
        : "DiarySidebarListItem--default";

    // Extract nested ternary into separate function
    const getDetailedLabelsClass = () => {
      if (!(parent.props as any).showDetailedLabels) {
        return "";
      }
      return selected
        ? "DiarySidebarListItem_SelectedItem--withDetailedLabels"
        : "DiarySidebarListItem--withDetailedLabels";
    };

    const detailedLabelsClass = getDetailedLabelsClass();

    const preorderClass = hasPreorderBanner() ? "has-preorder-banner" : "";
    return `${baseClass} ${viewClass} ${detailedLabelsClass} ${preorderClass}`.trim();
  };

  const swipeoutOptionClick = (name: string, booking: ViewBooking) => {
    // Reset any other swipeout components if needed
    // Note: EDSSwipeout handles its own reset internally

    // Close popover immediately to hide any visual updates
    setIsPopoverOpen(false);

    // Handle special actions that aren't stage progression
    if (name === "no-show") {
      // Set actualGuests to 0 and currentStage to "no-show" to mark as no-show and remove from runsheet
      const { bookings, setDiaryContext } = diaryContext;
      if (!bookings) return;

      const { updatedBookings, updatedBooking } = updateBooking({
        selectedBooking: booking,
        bookings,
        bookingPropsToSave: {
          actualGuests: 0,
          currentStage: "no-show"
        }
      });

      setDiaryContext({ bookings: updatedBookings });
      addOrUpdateBooking({
        booking: updatedBooking,
        diaryContext
      });
      return;
    }

    if (name === "left") {
      // Set stage to "left" for customers who have left
      const { bookings, setDiaryContext } = diaryContext;
      if (!bookings) return;

      const { updatedBookings, updatedBooking } = updateBooking({
        selectedBooking: booking,
        bookings,
        bookingPropsToSave: {
          currentStage: "left"
        }
      });

      setDiaryContext({ bookings: updatedBookings });
      addOrUpdateBooking({
        booking: updatedBooking,
        diaryContext
      });
      return;
    }

    // Get dynamic venue stages
    const stages =
      venueConfig && venueGroupConfig
        ? getVenueBookingStages({ venueConfig, venueGroupConfig })
        : [];

    // Handle "Uncheck" action
    if (name === "uncheck" || name === "awaiting") {
      updateBookingStage(name, booking, null, null);
      return;
    }

    // Handle dynamic venue stages (from dropdown or Check In button)
    const isVenueStage = stages.some((stage) => stage.id === name);
    if (isVenueStage) {
      updateBookingStage(name, booking, null, null);
      return;
    }

    // Original cycling logic for swipeout actions
    const nextStage = getNextBookingStageFromKey(
      currentStage ? currentStage.id : "",
      venueConfig,
      venueGroupConfig
    );

    const checkInWaitlistProps = getCheckInWaitlistPropsIfNeeded(booking);

    const nextStageWalkIn: BookingStage | null = getNextBookingStageFromKey(
      currentStageWalkIn ? currentStageWalkIn.id : "",
      venueConfig,
      venueGroupConfig
    );

    // Handle different action types
    // Note: These are user configurable stages that require architectural changes
    // for dynamic stage configuration. See work item AB#XXXXXX for implementation.
    updateBookingStage(name, booking, nextStage, nextStageWalkIn);

    // Close the popover immediately to prevent multiple clicks
    setIsPopoverOpen(false);

    // Only call onBookingChanged, not refreshBookings to avoid re-fetching
    onBookingChanged({ ...booking, ...checkInWaitlistProps });
  };
  const bookingTime = booking.time
    ? formatTimeForLocale(booking.time, user?.locale)
    : "No time";

  const waitlistTime = booking.waitlistDetails?.availableTime
    ? `Est. ${formatTimeForLocale(getBookingTimeAsString(booking), user?.locale)}`
    : "ASAP";

  const displayTime =
    isActivelyWaitlisted && !booking.currentStage ? waitlistTime : bookingTime;

  return (
    <div
      key={booking.id ?? booking.offlineId ?? `booking-${index}`}
      style={{
        ...style,
        display: "flex",
        position: "relative",
        top: 0
      }}
    >
      {/* Drag icon overlay - positioned absolute to bypass Swipeout/PullToRefresh */}
      <DiaryBookingCardDragButton
        selectedView={parent.props.selectedView}
        onClick={handleDragButtonClick}
        onPointerDown={handleDragButtonPointerDown}
        onPointerMove={handleDragButtonPointerMove}
        onPointerUp={handleDragButtonPointerUp}
        onPointerCancel={handleDragButtonPointerCancel}
        onLostPointerCapture={handleDragButtonLostPointerCapture}
        onDragStart={handleDragButtonDragStart}
        onDragEnd={handleDragButtonDragEnd}
      />
      <EDSSwipeout
        tabIndex={0}
        leftOptions={isUpdating ? [] : convertToEDSOptions(leftOptions)}
        rightOptions={isUpdating ? [] : convertToEDSOptions(rightOptions)}
        onOptionClick={(e) => swipeoutOptionClick(e, booking)}
        style={{
          width: "100%",
          position: "relative",
          marginRight: selectedView === "FloorplanTab" ? "32px" : "0"
        }}
      >
        <button
          id={`Booking_${booking.id}`}
          className={getBookingCardClassName()}
          onClick={handleBookingCardClick}
          onKeyDown={handleBookingCardKeyDown}
        >
          {/* Pre-order Banner - Only show for RunsheetBeta - Full width header */}
          <DiaryBookingCardPreorderBanner
            booking={booking}
            showDetailedLabels={(parent.props as any).showDetailedLabels}
            venueConfig={parent.props.venueConfig}
            isInitialising={diaryContext.isInitialising}
          />

          {/* Status Indicator — interactive for waitlisted bookings (stage dropdown on indicator) */}
          <DiaryBookingCardStatusIndicator
            booking={booking}
            venueConfig={parent.props.venueConfig}
            venueGroupConfig={parent.props.venueGroupConfig}
            isUpdating={isUpdating}
            optimisticStage={optimisticStage}
            isInteractive={isDesktop && isActivelyWaitlisted}
            isWaitlisted={isDesktop && isActivelyWaitlisted}
            onStageChange={(stageId: string) =>
              swipeoutOptionClick(stageId, booking)
            }
            isPopoverOpen={isPopoverOpen}
            onPopoverOpenChange={setIsPopoverOpen}
          />

          <div className="DiarySidebarListItem__details">
            {/* Customer Requests - Only show for RunsheetBeta */}
            <DiaryBookingCardCustomerNotes
              notes={booking.notes}
              bookingId={booking.id}
              showDetailedLabels={(parent.props as any).showDetailedLabels}
            />

            <div className="DiarySidebarListRow Row1">
              <span className="DiarySidebarListItem__detailsName">
                <DiaryBookingCardCustomerNameSection
                  booking={booking}
                  isOnRunSheet={(parent.props as any).isOnRunSheet}
                />
              </span>
              <div className="DiarySidebarListItem__auxTime">
                <div className="DiarySidebarListItem__auxBookTime">
                  {displayTime}
                </div>
                {/* Move details type to be adjacent to time for runsheet beta */}
                <DiaryBookingCardBookingTypeInTimeSection
                  bookingType={bookingType}
                  isOnRunSheet={(parent.props as any).isOnRunSheet}
                />
              </div>
            </div>

            <DiaryBookingCardTableAndZoneInfo
              isOnRunSheet={(parent.props as any).isOnRunSheet}
              booking={booking}
              venueConfig={parent.props.venueConfig}
            />

            {/* Runsheet-specific detailed information row */}
            <DiaryBookingCardRunsheetDetailsRow
              booking={booking}
              isOnRunSheet={(parent.props as any).isOnRunSheet}
              venueConfig={parent.props.venueConfig}
              venueGroupConfig={parent.props.venueGroupConfig}
            />

            {/* Customer Comments - Only show for RunsheetBeta */}
            <DiaryBookingCardCustomerComments
              comments={booking.comments}
              bookingId={booking.id}
              showDetailedLabels={(parent.props as any).showDetailedLabels}
            />

            {hasRow3Content() && (
              <div className="DiarySidebarListRow Row3">
                {booking.unsaved ? (
                  <DiaryBookingCardOfflineIndicator
                    unsaved={booking.unsaved}
                    bookingId={booking.id}
                    status={booking.status}
                  />
                ) : (
                  <div className="DiarySidebarListItem__IconsContainer">
                    <div
                      className="DiarySidebarListItem__Icons"
                      id={`bookingCard_${booking.id}`}
                      key={`bookingCard_${booking.id}`}
                    >
                      <BookingCardIcons
                        booking={booking}
                        isOnRunSheet={(parent.props as any).isOnRunSheet}
                      />
                    </div>
                  </div>
                )}

                {/* Right side content - Additional Labels */}
                <div className="DiarySidebarListItem__rightContent">
                  {/* Booking Type Badge - Right aligned (hidden for runsheet) */}
                  <DiaryBookingCardBookingTypeBadge
                    booking={booking}
                    isOnRunSheet={(parent.props as any).isOnRunSheet}
                  />

                  {/* Additional Labels - Removed old tag system in favor of icon-based display */}

                  {/* Detailed Labels for RunsheetBeta - Integrated within right content */}
                  <DiaryBookingCardDetailedLabels
                    booking={booking}
                    venueConfig={parent.props.venueConfig}
                    venueGroupConfig={parent.props.venueGroupConfig}
                    showDetailedLabels={
                      (parent.props as any).showDetailedLabels
                    }
                    isOnRunSheet={(parent.props as any).isOnRunSheet}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons - Only show for RunsheetBeta */}
            <DiaryBookingCardActionButtons
              booking={booking}
              isOnRunSheet={(parent.props as any).isOnRunSheet}
              isUpdating={isUpdating}
              optimisticStage={optimisticStage}
              currentStage={currentStage}
              venueConfig={parent.props.venueConfig}
              venueGroupConfig={parent.props.venueGroupConfig}
              isPopoverOpen={isPopoverOpen}
              setIsPopoverOpen={setIsPopoverOpen}
              setShowSendSms={(show: boolean) => {
                if (show) {
                  parent.props.setSelectedBooking(booking);
                }
                setShowSendSms(show);
              }}
              setShowPreviewModal={setShowPreviewModal}
              setShowEditBooking={setShowEditBooking}
              swipeoutOptionClick={swipeoutOptionClick}
              getFirstCheckedInStage={getFirstCheckedInStage}
              bookingSmsEnabled={parent.props.venueConfig?.booking_sms_enabled}
            />
          </div>
        </button>
      </EDSSwipeout>

      {/* Modals */}
      {showSendSms && (
        <SendSms handleClose={handleCloseSendSms} handleSent={handleSentSms} />
      )}

      {showPreviewModal && (
        <PreviewBookingModalBeta
          booking={booking}
          venueConfig={parent.props.venueConfig}
          venueGroupConfig={parent.props.venueGroupConfig}
          open={showPreviewModal}
          onClose={handleClosePreviewModal}
        />
      )}

      {showEditBooking && (
        <EditBooking
          booking={booking}
          venueConfig={parent.props.venueConfig}
          handleClose={handleCloseEditBooking}
        />
      )}
    </div>
  );
};
